/**
 * CreatorTokenList — Table/card list of creator's tokens with stats.
 *
 * Desktop: table layout with columns.
 * Mobile: card-based layout with stacked fields.
 * Each token has a "Manage Fees" toggle that expands FeeClaimCard + ClaimHistory.
 * Token name links to /token/{tokenId}.
 *
 * Story 6.2 — Task 4, Story 6.3 — Task 6
 */

import type { CreatorTokenWithStats } from "@repo/shared-types";
import { Link } from "@tanstack/react-router";
import { useState } from "react";

import { useSparklineData } from "../../hooks/api/use-sparkline-data";
import {
	formatCompact,
	formatCount,
	formatNativeCurrency,
	formatPrice,
	formatPriceChange,
} from "../../lib/format-number";
import { getNativeCurrencySymbol } from "../../lib/wagmi-config";
import { Sparkline } from "../ui/Sparkline";
import { ClaimHistory } from "./ClaimHistory";
import { FeeClaimCard } from "./FeeClaimCard";
import { FeeRevenueChart } from "./FeeRevenueChart";

/** Format accumulated fees (wei string) to compact native currency display */
function formatAccumulatedFees(token: CreatorTokenWithStats): string {
	if (!token.accumulatedFees) return "--";
	const available = BigInt(token.accumulatedFees.availableAmount);
	const claimed = BigInt(token.accumulatedFees.totalClaimedAmount);
	return formatNativeCurrency(available + claimed, getNativeCurrencySymbol());
}

type CreatorTokenListProps = {
	tokens: CreatorTokenWithStats[];
	isLoading: boolean;
};

function TokenRowSparkline({ tokenId }: { tokenId: string }) {
	const { data } = useSparklineData(tokenId);
	if (!data) return null;
	return <Sparkline data={data} width={60} height={20} className="hidden md:block" />;
}

function TokenRow({
	token,
	isExpanded,
	onToggleExpand,
}: {
	token: CreatorTokenWithStats;
	isExpanded: boolean;
	onToggleExpand: () => void;
}) {
	const priceChange = token.stats ? formatPriceChange(token.stats.priceChange24h) : null;
	const hasContractAddress = !!token.contractAddress;

	return (
		<div>
			{/* Desktop row */}
			<div className="hidden md:grid md:grid-cols-7 md:gap-4 md:items-center px-4 py-3 hover:bg-pastel-light-gray/50 transition-colors rounded-lg">
				<Link
					to="/token/$tokenId"
					params={{ tokenId: token.id }}
					className="flex items-center gap-3"
				>
					<div className="h-8 w-8 rounded-full bg-pastel-light-gray flex items-center justify-center text-xs font-bold text-pastel-gray">
						{token.symbol.slice(0, 2)}
					</div>
					<div>
						<p className="text-sm font-semibold text-pastel-black hover:text-pastel-vibrant-blue transition-colors">
							{token.name}
						</p>
						<p className="text-xs text-pastel-gray">{token.symbol}</p>
					</div>
				</Link>
				<div className="text-right flex items-center justify-end gap-2">
					<TokenRowSparkline tokenId={token.id} />
					<div>
						<p className="text-sm font-medium text-pastel-black">
							{token.stats ? formatPrice(token.stats.price) : "--"}
						</p>
						{priceChange && (
							<p className={`text-xs font-medium ${priceChange.className}`}>{priceChange.text}</p>
						)}
					</div>
				</div>
				<p className="text-sm text-pastel-black text-right">
					{token.stats ? formatCompact(token.stats.volume24h) : "--"}
				</p>
				<p className="text-sm text-pastel-black text-right">
					{token.stats ? formatCount(token.stats.holderCount) : "--"}
				</p>
				<p className="text-sm text-pastel-black text-right">{formatAccumulatedFees(token)}</p>
				<p className="text-sm text-pastel-black text-right">
					{token.stats ? formatCount(token.stats.txCount24h) : "--"}
				</p>
				<div className="text-right">
					{hasContractAddress && (
						<button
							type="button"
							onClick={onToggleExpand}
							className="text-sm font-medium text-pastel-vibrant-blue hover:underline"
						>
							{isExpanded ? "Hide" : "Manage Fees"}
						</button>
					)}
				</div>
			</div>

			{/* Mobile card */}
			<div className="md:hidden rounded-xl border border-pastel-border bg-white p-4">
				<Link to="/token/$tokenId" params={{ tokenId: token.id }} className="block">
					<div className="flex items-center gap-3 mb-3">
						<div className="h-10 w-10 rounded-full bg-pastel-light-gray flex items-center justify-center text-sm font-bold text-pastel-gray">
							{token.symbol.slice(0, 2)}
						</div>
						<div>
							<p className="text-sm font-semibold text-pastel-black">{token.name}</p>
							<p className="text-xs text-pastel-gray">{token.symbol}</p>
						</div>
						<div className="ml-auto text-right">
							<p className="text-sm font-bold text-pastel-black">
								{token.stats ? formatPrice(token.stats.price) : "--"}
							</p>
							{priceChange && (
								<p className={`text-xs font-medium ${priceChange.className}`}>{priceChange.text}</p>
							)}
						</div>
					</div>
					<div className="grid grid-cols-3 gap-2 text-center">
						<div>
							<p className="text-xs text-pastel-gray">Volume</p>
							<p className="text-sm font-medium text-pastel-black">
								{token.stats ? formatCompact(token.stats.volume24h) : "--"}
							</p>
						</div>
						<div>
							<p className="text-xs text-pastel-gray">Holders</p>
							<p className="text-sm font-medium text-pastel-black">
								{token.stats ? formatCount(token.stats.holderCount) : "--"}
							</p>
						</div>
						<div>
							<p className="text-xs text-pastel-gray">Your Fees</p>
							<p className="text-sm font-medium text-pastel-black">
								{formatAccumulatedFees(token)}
							</p>
						</div>
					</div>
				</Link>
				{hasContractAddress && (
					<button
						type="button"
						onClick={onToggleExpand}
						className="mt-3 w-full rounded-lg border border-pastel-border px-4 py-2 text-sm font-medium text-pastel-vibrant-blue hover:bg-pastel-light-gray/50 transition-colors"
					>
						{isExpanded ? "Hide Fees" : "Manage Fees"}
					</button>
				)}
			</div>

			{/* Expanded fee claim section */}
			{isExpanded && token.contractAddress && (
				<div className="mt-2 md:ml-4 space-y-4 pb-4">
					<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
						<FeeClaimCard
							tokenId={token.id}
							tokenAddress={token.contractAddress}
							rewardRecipient={token.creatorWallet}
						/>
						<ClaimHistory tokenId={token.id} />
					</div>
					<FeeRevenueChart tokenId={token.id} />
				</div>
			)}
		</div>
	);
}

function TokenListSkeleton() {
	return (
		<div className="space-y-3 animate-pulse">
			{Array.from({ length: 4 }).map((_, i) => (
				<div
					key={`token-skeleton-${String(i)}`}
					className="rounded-xl border border-pastel-border bg-white p-4"
				>
					<div className="flex items-center gap-3">
						<div className="h-10 w-10 rounded-full bg-pastel-light-gray" />
						<div className="flex-1">
							<div className="h-4 w-24 bg-pastel-light-gray rounded mb-1" />
							<div className="h-3 w-12 bg-pastel-light-gray rounded" />
						</div>
						<div className="h-4 w-16 bg-pastel-light-gray rounded" />
					</div>
				</div>
			))}
		</div>
	);
}

function TableHeader() {
	return (
		<div className="hidden md:grid md:grid-cols-7 md:gap-4 px-4 py-2 text-xs font-medium text-pastel-gray uppercase tracking-wide">
			<span>Token</span>
			<span className="text-right">Price</span>
			<span className="text-right">24h Volume</span>
			<span className="text-right">Holders</span>
			<span className="text-right">Your Fees</span>
			<span className="text-right">24h Txns</span>
			<span className="text-right">Actions</span>
		</div>
	);
}

export function CreatorTokenList({ tokens, isLoading }: CreatorTokenListProps) {
	const [expandedTokenId, setExpandedTokenId] = useState<string | null>(null);

	if (isLoading) {
		return <TokenListSkeleton />;
	}

	return (
		<div>
			<TableHeader />
			<div className="space-y-2 md:space-y-0">
				{tokens.map((token) => (
					<TokenRow
						key={token.id}
						token={token}
						isExpanded={expandedTokenId === token.id}
						onToggleExpand={() =>
							setExpandedTokenId((prev) => (prev === token.id ? null : token.id))
						}
					/>
				))}
			</div>
		</div>
	);
}
